import React, { useEffect, useState } from 'react';
import { View, Text, ScrollView, StyleSheet } from 'react-native';
import Checkbox from 'expo-checkbox';

import { BasicModalContainer, ModalHeader, ModalBody, ModalFooter } from '../../../../common/components/basicmodal';
import { BOHButton } from '../../../../common/components/bohtoolbar';
import { useAlertModal } from '../../../../common/hooks';
import { saveProductColumnConfig } from '../../../../api/Product';

// Column definitions shared with the Products list. `meta:<id>` ids refer to
// custom fields defined under Settings → Inventory Fields.
export interface ColumnDef {
  id: string;
  label: string;
  width?: number;
  sortKey: string;
  sub?: string;
}

export const BASE_COLUMNS: ColumnDef[] = [
  { id: 'category', label: 'Category', sortKey: 'category' },
  { id: 'family', label: 'Family', width: 200, sortKey: 'family' },
  { id: 'line', label: 'Line', width: 200, sortKey: 'line' },
  { id: 'size', label: 'Size', width: 80, sortKey: 'size' },
  { id: 'barcode', label: 'Barcode', sortKey: 'barcode' },
  { id: 'license_number', label: 'License #', sortKey: 'license_number' },
  { id: 'home_location', label: 'Home location', sortKey: 'home_location', sub: '(Inventory Count)' },
  { id: 'current_location', label: 'Current location', sortKey: 'current_location' },
  { id: 'status', label: 'Status', width: 110, sortKey: 'status' },
  { id: 'tracking_tag', label: 'Tracking Tags', width: 150, sortKey: 'tracking_tag' },
  { id: 'last_scanned_at', label: 'Last Scanned Date(Time)', width: 160, sortKey: 'last_scanned_at' },
];

export const buildColumns = (productFields: any[]): ColumnDef[] => {
  const metaCols = (productFields || [])
    .filter(f => f.active)
    .map(f => ({
      id: `meta_${f.id}`,
      label: f.label,
      width: 150,
      sortKey: `meta_${f.id}`,
    }));
  return [...BASE_COLUMNS, ...metaCols];
};

const ColumnPickerModal = ({ isModalVisible, columns, selectedIds, closeModal, onSaved }) => {
  const { showAlert } = useAlertModal();
  const [selected, setSelected] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isModalVisible) setSelected(selectedIds || []);
  }, [isModalVisible, selectedIds]);

  const toggle = (id: string) => {
    setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const save = () => {
    if (!selected.length) {
      showAlert('warning', 'Select at least one column');
      return;
    }
    setSaving(true);
    saveProductColumnConfig(selected, (jsonRes, status) => {
      setSaving(false);
      if (status === 200) {
        showAlert('success', 'Columns saved');
        onSaved(selected);
        closeModal();
      } else {
        showAlert('error', jsonRes?.error || 'Failed to save columns');
      }
    });
  };

  return isModalVisible ? (
    <View style={{ position: 'absolute', width: '100%', height: '100%' }}>
      <BasicModalContainer>
        <ModalHeader label="Choose Columns" closeModal={closeModal} />
        <ModalBody>
          <ScrollView style={{ maxHeight: 420 }}>
            {columns.map(col => (
              <View key={col.id} style={styles.row}>
                <Checkbox value={selected.includes(col.id)} onValueChange={() => toggle(col.id)} />
                <Text style={styles.label}>{col.label}{col.sub ? ` ${col.sub}` : ''}</Text>
              </View>
            ))}
          </ScrollView>
        </ModalBody>
        <ModalFooter>
          <BOHButton label={saving ? 'Saving…' : 'Save'} onPress={save} disabled={saving} />
        </ModalFooter>
      </BasicModalContainer>
    </View>
  ) : null;
};

const styles = StyleSheet.create({
  row: { flexDirection: 'row', alignItems: 'center', paddingVertical: 6 },
  label: { marginLeft: 10 },
});

export default ColumnPickerModal;
